import * as React from "react";
import { IconButton } from "../icon-button";
import { Thumbnail } from "../thumbnail";

interface CarouselProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  children?: React.ReactNode;
}

const CHEVRON_COUNT = 2;
// Mirrors IconButton size="sm", which uses --spacing-32.
const SM_ICON_BUTTON_SIZE_PX = 32;
const MIN_CHEVRON_ITEM_COUNT = 3;

function getCarouselChild(child: React.ReactNode): React.ReactNode {
  if (
    !React.isValidElement<React.ComponentProps<typeof Thumbnail>>(child) ||
    child.type !== Thumbnail
  ) {
    return child;
  }

  return React.cloneElement(child, {
    selected: child.props.selected ?? false,
  });
}

function getCarouselWidth(element: HTMLElement): number {
  return element.clientWidth || element.getBoundingClientRect().width;
}

function getThumbnailTrackWidth(track: HTMLElement): number {
  return Math.max(track.scrollWidth, track.getBoundingClientRect().width);
}

function getThumbnailViewportWidth(carouselWidth: number): number {
  const chevronsWidth = SM_ICON_BUTTON_SIZE_PX * CHEVRON_COUNT;

  return Math.max(0, carouselWidth - chevronsWidth);
}

function getVisibleItemCount(
  viewport: HTMLElement,
  track: HTMLElement,
): number {
  const viewportWidth = getCarouselWidth(viewport);
  const items = Array.from(track.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement,
  );

  if (viewportWidth <= 0 || items.length === 0) return items.length;

  let visibleCount = 0;

  for (const item of items) {
    const itemWidth = item.offsetWidth || item.getBoundingClientRect().width;

    if (itemWidth <= 0) return items.length;
    if (item.offsetLeft + itemWidth > viewportWidth) break;

    visibleCount += 1;
  }

  return Math.max(1, visibleCount);
}

/**
 * Thumbnail carousel with looping previous and next navigation.
 *
 * @example
 * <Carousel>
 *   <Thumbnail src={src1} alt="Motor drive front view" selected />
 *   <Thumbnail src={src2} alt="Motor drive nameplate" />
 * </Carousel>
 */
function Carousel({ children, ...props }: CarouselProps) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const updateOverflowRef = React.useRef<() => void>(() => {});
  const [showChevrons, setShowChevrons] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [visibleItemCount, setVisibleItemCount] = React.useState(
    Number.POSITIVE_INFINITY,
  );
  const childArray = React.Children.toArray(children);
  const itemCount = childArray.length;
  const normalizedOffset = itemCount > 0 ? offset % itemCount : 0;
  const items = childArray.map((child, index) => ({
    child: getCarouselChild(child),
    key: index,
  }));
  const visibleItems = showChevrons
    ? [...items.slice(normalizedOffset), ...items.slice(0, normalizedOffset)]
    : items;

  updateOverflowRef.current = () => {
    const root = rootRef.current;
    const viewport = viewportRef.current;
    const track = trackRef.current;

    if (!root || !viewport || !track) return;

    const carouselWidth = getCarouselWidth(root);
    const thumbnailTrackWidth = getThumbnailTrackWidth(track);
    const thumbnailViewportWidth = getThumbnailViewportWidth(carouselWidth);
    const overflowsCarousel = thumbnailTrackWidth > carouselWidth;
    const overflowsThumbnailViewport =
      itemCount >= MIN_CHEVRON_ITEM_COUNT &&
      thumbnailTrackWidth > thumbnailViewportWidth;
    const shouldShowChevrons =
      itemCount > 1 &&
      carouselWidth > 0 &&
      (overflowsCarousel || overflowsThumbnailViewport);

    setShowChevrons(shouldShowChevrons);
    setVisibleItemCount(
      shouldShowChevrons ? getVisibleItemCount(viewport, track) : itemCount,
    );

    if (!shouldShowChevrons) {
      setOffset(0);
    }
  };

  React.useLayoutEffect(() => {
    updateOverflowRef.current();
  });

  React.useLayoutEffect(() => {
    const root = rootRef.current;
    const viewport = viewportRef.current;
    const track = trackRef.current;

    if (!root || !viewport || !track || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateOverflowRef.current();
    });
    observer.observe(root);
    observer.observe(viewport);
    observer.observe(track);

    return () => observer.disconnect();
  }, []);

  const handleNext = () => {
    if (itemCount === 0) return;

    setOffset((currentOffset) => (currentOffset + 1) % itemCount);
  };

  const handlePrev = () => {
    if (itemCount === 0) return;

    setOffset((currentOffset) => (currentOffset - 1 + itemCount) % itemCount);
  };

  return (
    <div
      {...props}
      ref={rootRef}
      data-slot="carousel"
      className={
        showChevrons
          ? "flex w-full items-center"
          : "flex w-full items-center gap-(--spacing-10)"
      }
    >
      {showChevrons && (
        <IconButton
          variant="ghost"
          size="sm"
          icon="chevron-left"
          aria-label="Previous Images"
          onClick={handlePrev}
        />
      )}
      <div
        ref={viewportRef}
        data-slot="carousel-viewport"
        className={
          showChevrons
            ? "min-w-0 flex-1 overflow-hidden"
            : "min-w-0 max-w-full overflow-hidden"
        }
      >
        <div
          ref={trackRef}
          data-slot="carousel-track"
          className="relative flex w-max items-center gap-(--spacing-10) p-(--spacing-4)"
        >
          {visibleItems.map(({ child, key }, index) => {
            const isClipped = showChevrons && index >= visibleItemCount;

            return (
              <div
                key={key}
                data-slot="carousel-item"
                className="shrink-0"
                inert={isClipped ? true : undefined}
                aria-hidden={isClipped || undefined}
              >
                {child}
              </div>
            );
          })}
        </div>
      </div>
      {showChevrons && (
        <IconButton
          variant="ghost"
          size="sm"
          icon="chevron-right"
          aria-label="Next Images"
          onClick={handleNext}
        />
      )}
    </div>
  );
}

export { Carousel };
export type { CarouselProps };
